import format from "date-fns/format";
import isValid from "date-fns/isValid";
import styled from "styled-components";
import { ChangeEventHandler, FocusEventHandler, KeyboardEventHandler, useEffect, useMemo, useState } from "react";
import InputMask from "react-input-mask";
import { convertToAPIDate } from "manifest-core/utils/convertAPIDate";
import { ReactComponent as CalendarIcon } from "../Images/icons/Calendar.svg";
import { ColorVariables } from "../Colors";

// The default desktop safari version of the date input is pretty bad, so we don't show it
function isDateInputSupported() {
  const userAgent = navigator.userAgent.toLowerCase();
  // const isApplePhone = userAgent.match(/(ipad)|(iphone)|(ipod)/i);
  if (userAgent.indexOf("safari") > -1 && userAgent.indexOf("chrome") === -1) {
    return false;
  }
  const input = document.createElement("input");
  const value = "a";
  input.setAttribute("type", "date");
  input.setAttribute("value", value);
  return input.value !== value;
}

export type DateProps = Partial<{
  autoComplete: boolean | string;
  id: string;
  name: string;
  value: Date | string;
  format: string;
  onChange: (date?: Date) => any;
  onBlur: ChangeEventHandler<HTMLInputElement>;
  onFocus: ChangeEventHandler<HTMLInputElement>;
  onKeyDown: KeyboardEventHandler<HTMLInputElement>;
  maxDate?: Date;
  minDate?: Date;
}>;

const StyledCalendarIcon = styled(CalendarIcon)`
  height: 18px;
  width: 18px;
  position: absolute;
  top: 0;
  right: 0;
  margin-right: 14px;
  margin-top: 18px;
  fill: ${ColorVariables.mint};
  pointer-events: none;
`;

export default function DateInput(props: DateProps = {}) {
  const [localValue, setLocalValue] = useState("");
  const { maxDate, minDate } = props;

  // Autocomplete can either be a string for a unique key, on as regular, or disabled
  let autoCompleteValue = "off";
  if (typeof props.autoComplete === "string") autoCompleteValue = props.autoComplete;
  else if (typeof props.autoComplete === "boolean" && props.autoComplete) autoCompleteValue = "";

  const handleOnBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    // We have to add the time in here because a new date of 10-19-1987 will subtract a day
    let valueToDate = new Date(`${localValue} 12:00:00`);
    if (maxDate && valueToDate > maxDate) valueToDate = maxDate;
    if (minDate && valueToDate < minDate) valueToDate = minDate;

    if (props.onChange) {
      if (isValid(valueToDate)) {
        props.onChange(valueToDate);
      } else {
        props.onChange(undefined);
      }
    }
    if (props.onBlur) props.onBlur(e);
  };

  const maxMinDateDisplay = useMemo(() => {
    const formatted = {
      // eslint-disable-next-line sonarjs/no-duplicate-string
      minDate: format(new Date(minDate || new Date()), "MM/dd/yyyy"),
      maxDate: format(new Date(maxDate || new Date()), "MM/dd/yyyy"),
    };
    if (minDate && maxDate) {
      return (
        <p className="xsmall">
          * Date must be between {formatted.minDate} - {formatted.maxDate}
        </p>
      );
    }
    if (maxDate) {
      return <p className="xsmall">* Date must be no later than {formatted.maxDate}</p>;
    }
    if (minDate) {
      return <p className="xsmall">* Date must be no less than {formatted.minDate}</p>;
    }
    return null;
  }, [minDate, maxDate]);

  useEffect(() => {
    if (!props.value) {
      setLocalValue("");
    }
    if (props.value && isValid(new Date(props.value))) {
      if (isDateInputSupported()) {
        setLocalValue(format(new Date(props.value), "yyyy-MM-dd"));
      } else {
        setLocalValue(format(new Date(props.value), "MM/dd/yyyy"));
      }
    }
  }, [props.value]);

  const handleDateInputClick = (e) => {
    if (props.name === "dateOfBirth") {
      e.preventDefault();
    }
  };

  return (
    <>
      {isDateInputSupported() ? (
        <>
          <input
            type="date"
            name={props.name}
            data-name={props.name}
            data-lpignore={props.autoComplete ? "" : true}
            min={minDate ? convertToAPIDate(minDate) : undefined}
            max={maxDate ? convertToAPIDate(maxDate) : undefined}
            autoComplete={autoCompleteValue}
            id={props.id}
            onChange={(e) => setLocalValue(e.target.value)}
            value={localValue}
            onBlur={handleOnBlur}
            onKeyDown={props.onKeyDown}
            onFocus={props.onFocus}
            onClick={handleDateInputClick}
            required
          />
          {props.name !== "dateOfBirth" && <StyledCalendarIcon data-name="calendarIcon" />}
        </>
      ) : (
        <>
          <InputMask
            mask="99/99/9999"
            data-name={props.name}
            name={props.name}
            maskPlaceholder="MM/DD/YYYY"
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleOnBlur}
            onKeyDown={props.onKeyDown}
            onFocus={props.onFocus}
          />
          {maxMinDateDisplay}
        </>
      )}
    </>
  );
}
